import { useState } from "react";
import moment from "moment";
import { Switch } from "@mui/material";
import { Link } from "react-router-dom";
import { useAuthContextValueHook } from "@/contexts/AuthContext";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { useUserInfoQuery } from "@/hooks/ReactQuery/UserInfoReactQuery/UserInfoReactQuery";
import { ProBadge, useGoPlanTab, usePagePro } from "../FdSubscription/proPlan";
import {
    useDeleteProposalMutation,
    useProposalsQuery,
    useUpdateProposalMutation,
    useUpdateProposalSettingsMutation,
} from "@/hooks/ReactQuery/ProposalReactQuery/ProposalReactQuery";

const NOTIFY_OPTIONS = [
    { key: 'notifyEach', label: '새 제안마다 알림 받기', hint: '제안이 들어올 때마다 바로 문자를 보내요. (슈룩 Pro)', pro: true },
    { key: 'notifyDaily', label: '매일 종합해서 알림 받기', hint: '매일 낮 12시에 확인하지 않은 제안이 있으면 하루 한 번 보내요.' },
];

/**
 * 받은 제안 (공개 페이지 "제안하기"로 들어온 글). 펼치면 읽음 처리.
 */
export function FdProposal({ onRefreshPreviewViewer }) {
    const userPage = useUserPageContextValueHook()?.userPage;
    const userPageId = userPage?.id;
    const enabled = userPage?.proposalConfig?.enabled === true;
    const isPro = usePagePro();
    const goPlan = useGoPlanTab();
    // 값이 없으면 매 제안 알림 꺼짐, 매일 종합 알림 켬 (서버 UserPageEntity 와 같은 기본값)
    const notify = {
        notifyEach: userPage?.proposalConfig?.notifyEach === true,
        notifyDaily: userPage?.proposalConfig?.notifyDaily !== false,
    };
    // 제안 받기는 휴대전화 인증(프로필 > 휴대전화 설정)한 운영자만 켤 수 있다
    const authContextValueHook = useAuthContextValueHook();
    const userInfoQuery = useUserInfoQuery({ isLoggedIn: authContextValueHook?.isLoggedIn, isReady: authContextValueHook?.isReady, accessToken: authContextValueHook?.accessToken });
    const phoneVerified = !!userInfoQuery?.data?.data?.data?.phoneNumber;
    const needPhone = userInfoQuery?.isSuccess && !phoneVerified;
    const updateProposalSettingsMutation = useUpdateProposalSettingsMutation();
    const proposalsQuery = useProposalsQuery({ userPageId });
    const updateProposalMutation = useUpdateProposalMutation();
    const deleteProposalMutation = useDeleteProposalMutation();

    const [openId, setOpenId] = useState(null);

    const pages = proposalsQuery?.data?.pages;
    const items = pages?.flatMap(page => page.items) || [];
    const total = pages?.[0]?.total || 0;
    const unreadCount = pages?.[0]?.unreadCount || 0;

    const saveSettings = (settings) => {
        updateProposalSettingsMutation.mutate({ body: { userPageId, ...settings } }, { onSuccess: () => onRefreshPreviewViewer?.() });
    }

    const handleToggle = (item) => {
        const opening = openId !== item.id;
        setOpenId(opening ? item.id : null);
        if (opening && !item.isRead) {
            updateProposalMutation.mutate({ id: item.id, body: { isRead: true } });
        }
    }

    const handleMarkUnread = (item) => {
        setOpenId(null);
        updateProposalMutation.mutate({ id: item.id, body: { isRead: false } });
    }

    const handleDelete = (item) => {
        if (!window.confirm('삭제한 제안은 되돌릴 수 없어요. 삭제할까요?')) {
            return;
        }
        deleteProposalMutation.mutate({ id: item.id });
    }

    return (
        <div className="flex flex-col gap-5">
            <section className="rounded-[15px] border border-gray-100 shadow-[var(--defaultBoxShadow)] bg-white p-5 min-[993px]:p-7 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <h2 className="text-base font-bold">제안 받기 <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-bold ${enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{enabled ? '켜짐' : '꺼짐'}</span></h2>
                        {needPhone && !enabled
                            ? <p className="text-xs text-gray-500 mt-1 break-keep">
                                제안에는 연락처가 담겨 있어 <b className="text-gray-700">휴대전화 인증</b>을 한 운영자만 받을 수 있어요.{' '}
                                <Link to="/profile" className="font-semibold text-[var(--mainColor)] underline underline-offset-2">휴대전화 인증하러 가기 →</Link>
                            </p>
                            : <p className="text-xs text-gray-500 mt-1 break-keep">
                                {enabled
                                    ? '내 페이지에 ‘제안하기’ 버튼이 보여요. 끄면 버튼이 사라지고 새 제안을 받지 않아요.'
                                    : '내 페이지에 ‘제안하기’ 버튼이 보이지 않아요. 켜면 방문자가 제안을 보낼 수 있어요.'}
                            </p>
                        }
                    </div>
                    <Switch
                        color='success'
                        checked={enabled}
                        disabled={!userPageId || updateProposalSettingsMutation.isPending || (!enabled && !phoneVerified)}
                        onChange={(e) => saveSettings({ enabled: e.target.checked })}
                        inputProps={{ 'aria-label': '제안 받기' }}
                    />
                </div>

                <div className={`flex flex-col divide-y divide-gray-100 rounded-xl bg-gray-50 px-4 ${enabled ? '' : 'opacity-50'}`}>
                    <p className="py-3 text-[13px] font-bold text-gray-700">문자 알림 <span className="font-normal text-gray-400">프로필에 등록한 휴대전화로 보내요</span></p>
                    {NOTIFY_OPTIONS.map(option => (
                        <label key={option.key} className="flex items-center justify-between gap-4 py-2.5">
                            <span className="min-w-0">
                                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">{option.label}{option.pro && <ProBadge />}</span>
                                <span className="block text-xs text-gray-500 break-keep">{option.hint}</span>
                            </span>
                            <Switch
                                size='small'
                                color='success'
                                checked={notify[option.key] && (!option.pro || isPro)}
                                disabled={!enabled || updateProposalSettingsMutation.isPending}
                                onChange={(e) => (option.pro && !isPro ? goPlan() : saveSettings({ [option.key]: e.target.checked }))}
                                inputProps={{ 'aria-label': option.label }}
                            />
                        </label>
                    ))}
                </div>
            </section>

            <section className="rounded-[15px] border border-gray-100 shadow-[var(--defaultBoxShadow)] bg-white p-5 min-[993px]:p-7 flex flex-col">
                <div className="flex items-baseline justify-between gap-2">
                    <h2 className="text-xl font-bold">
                        받은 제안 <span className="text-base font-semibold text-[var(--mainColor)]">{total}</span>
                    </h2>
                    {unreadCount > 0 && <span className="rounded-full bg-[var(--redColor1)] px-2.5 py-0.5 text-xs font-bold text-white">새 제안 {unreadCount}</span>}
                </div>
                <p className="text-xs text-gray-500 mt-1">내 페이지의 &lsquo;제안하기&rsquo;로 들어온 글이에요. 연락처가 담겨 있어 1년이 지나면 자동으로 지워져요.</p>

                {proposalsQuery?.isLoading &&
                    <ul className="mt-2 flex flex-col divide-y" aria-busy="true">
                        {[0, 1, 2].map(i => (
                            <li key={i} className="py-4 flex flex-col gap-2 animate-pulse">
                                <div className="h-4 w-48 rounded bg-gray-100" />
                                <div className="h-3 w-full rounded bg-gray-100" />
                            </li>
                        ))}
                    </ul>
                }

                {!proposalsQuery?.isLoading && items.length === 0 &&
                    <div className="py-12 flex flex-col items-center gap-3 text-center">
                        <div className="w-14 h-14 rounded-full bg-[var(--mainColor100)] flex items-center justify-center text-2xl" aria-hidden="true">✉️</div>
                        <p className="text-sm font-semibold">아직 받은 제안이 없어요</p>
                        <p className="text-xs text-gray-500">방문자가 내 페이지의 &lsquo;제안하기&rsquo; 버튼으로 보낸 글이 여기에 모여요.</p>
                    </div>
                }

                {items.length > 0 &&
                    <ul className="mt-2 flex flex-col divide-y divide-gray-100">
                        {items.map(item => {
                            const open = openId === item.id;
                            return (
                                <li key={item.id} className="py-1">
                                    <button
                                        type="button"
                                        onClick={() => handleToggle(item)}
                                        aria-expanded={open}
                                        className="flex w-full items-start gap-3 rounded-xl px-2 py-3 text-left hover:bg-gray-50"
                                    >
                                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.isRead ? 'bg-transparent' : 'bg-[var(--mainColor)]'}`} aria-label={item.isRead ? undefined : '안 읽음'} />
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-baseline justify-between gap-3">
                                                <span className={`truncate text-[15px] ${item.isRead ? 'font-medium text-gray-700' : 'font-bold text-gray-900'}`}>{item.subject}</span>
                                                <span className="shrink-0 text-xs tabular-nums text-gray-400">{moment(item.createdAt).format('YYYY.MM.DD HH:mm')}</span>
                                            </span>
                                            {!open && <span className="mt-0.5 block truncate text-[13px] text-gray-500">{item.content}</span>}
                                        </span>
                                    </button>

                                    {open &&
                                        <div className="mb-3 ml-7 mr-2 flex flex-col gap-3 rounded-xl bg-gray-50 p-4">
                                            <p className="whitespace-pre-line break-words text-[14px] leading-relaxed text-gray-800">{item.content}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {item.email &&
                                                    <a href={`mailto:${item.email}?subject=${encodeURIComponent(`Re: ${item.subject}`)}`} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-[13px] font-semibold text-gray-800 ring-1 ring-black/5 hover:bg-gray-100">
                                                        ✉️ {item.email}
                                                    </a>
                                                }
                                                {item.phone &&
                                                    <a href={`tel:${item.phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-[13px] font-semibold text-gray-800 ring-1 ring-black/5 hover:bg-gray-100">
                                                        📞 {item.phone}
                                                    </a>
                                                }
                                            </div>
                                            <div className="flex justify-end gap-1">
                                                <button type="button" onClick={() => handleMarkUnread(item)} className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-gray-500 hover:bg-gray-200">안 읽음으로</button>
                                                <button type="button" onClick={() => handleDelete(item)} className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[var(--redColor1)] hover:bg-red-50">삭제</button>
                                            </div>
                                        </div>
                                    }
                                </li>
                            );
                        })}
                    </ul>
                }

                {proposalsQuery?.hasNextPage &&
                    <button
                        type="button"
                        onClick={() => proposalsQuery.fetchNextPage()}
                        disabled={proposalsQuery.isFetchingNextPage}
                        className="mt-3 self-center rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                        더 보기
                    </button>
                }
            </section>
        </div>
    );
}
